import { type MouseEvent, type PointerEvent, useRef, useState } from "react";
import { chartHeight, chartMargin, chartWidth } from "./shared";

/** How far, in screen pixels, a press moves before it reads as a drag rather than a click. */
const DRAG_THRESHOLD = 4;

const left = chartMargin.left;
const right = chartWidth - chartMargin.right;

/** Where the pointer sits across the plot, in chart units. */
function chartXOf(event: PointerEvent<SVGSVGElement>) {
  const box = event.currentTarget.getBoundingClientRect();
  if (box.width === 0) return left;
  const x = ((event.clientX - box.left) / box.width) * chartWidth;
  return Math.min(Math.max(x, left), right);
}

/**
 * Lets a chart be dragged across to pick a span of time. A press that barely moves stays a click;
 * a drag swallows the click that follows it. Spread `svgProps`, given what turns chart units into
 * times, on the chart's svg, and draw `band` inside it.
 */
export function useDragZoom(onZoom: ((from: number, to: number) => void) | undefined) {
  const [drag, setDrag] = useState<{ start: number; end: number } | null>(null);
  const press = useRef<{ clientX: number; chartX: number } | null>(null);
  const dragged = useRef(false);
  if (!onZoom) return { svgProps: () => ({}), band: null };

  const svgProps = (invert: (chartX: number) => number) => ({
    onPointerDown: (event: PointerEvent<SVGSVGElement>) => {
      dragged.current = false;
      if (event.button !== 0) return;
      press.current = { clientX: event.clientX, chartX: chartXOf(event) };
    },
    onPointerMove: (event: PointerEvent<SVGSVGElement>) => {
      const pressed = press.current;
      if (!pressed) return;
      if (!dragged.current) {
        if (Math.abs(event.clientX - pressed.clientX) < DRAG_THRESHOLD) return;
        dragged.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
      }
      setDrag({ start: pressed.chartX, end: chartXOf(event) });
    },
    onPointerUp: () => {
      press.current = null;
      if (drag) {
        const from = invert(Math.min(drag.start, drag.end));
        const to = invert(Math.max(drag.start, drag.end));
        onZoom(from, to);
      }
      setDrag(null);
    },
    onPointerCancel: () => {
      press.current = null;
      dragged.current = false;
      setDrag(null);
    },
    onClickCapture: (event: MouseEvent<SVGSVGElement>) => {
      if (!dragged.current) return;
      dragged.current = false;
      event.stopPropagation();
    },
  });
  const band = drag && (
    <rect
      x={Math.min(drag.start, drag.end)}
      y={chartMargin.top}
      width={Math.abs(drag.end - drag.start)}
      height={chartHeight - chartMargin.top - chartMargin.bottom}
      className="pointer-events-none fill-foreground/10 stroke-foreground/30"
    />
  );
  return { svgProps, band };
}
